"use client";

import React from "react";
import Image from "next/image";
import s from "../styles.parts";
import Consts from "@common/config/consts";
import { logjs, utils } from "@lib/client";
import { usePartsList } from "../usePartsList";

const log = new logjs("PartsConfirmArea");
/**
 * 部品情報確認表示コンポーネント
 * partsconfirm.ctpをReactに移植した表示専用コンポーネント

 */
export default function PartsConfirmArea({
  data,
  state,
  actions,
  form: parentForm,
  formProps: parentFormProps,
  partsData,
  addPartsData,
  quote_number,
  photoDialogProps,
  fileupDialogProps,
  resPict = [],
}) {
  log.debug("START ");

  // 共通関数の取得
  const [form, formProps] = usePartsList({}, state, actions);
  const { corp } = state?.initData || {};
  // 現車と紐付いている場合は色変える
  const isGensha = data?.request?.GenshaID || false;
  const partskind = state?.initData?.partskind || [];
  // 依頼期限の判定
  const isAfterCarryTaxinDate = formProps.isAfterCarryTaxinDate(data?.request?.Request_Limit);

  // 損保からの依頼分の判定
  const isInsuranceRequest = data?.request?.CorpID?.startsWith("4") && Consts.RE_PARTS_CORP.includes(corp?.CorpID);

  // 追加部品の画像有無確認
  const hasAddPartsPhoto = data?.hasAddPartsPhoto;

  if (!data || !data.partsList) {
    return null;
  }

  // null/undefined 時は空オブジェクトで扱い、プロパティ参照エラーを防ぐ
  const safePartsData = partsData ?? {};
  const safeAddPartsData = addPartsData ?? {};

  log.debug("-------------------data", { data, partsData, addPartsData });

  let mergePartsList = utils.deepClone(data.partsList);
  for (const key in safePartsData) {
    const _parts_item = data.partsList.find((item) => item.id == key);
    const is_parts_item = !utils.isNull(_parts_item);
    log.debug(`is_parts_item:${is_parts_item} key:${key}`, _parts_item);
    if (is_parts_item == false) {
      const arr = safePartsData[key];
      const first = Array.isArray(arr) ? arr[0] : arr;
      if (first) {
        let _data = { ...first, OthParts4CD: key };
        mergePartsList.push(_data);
      }
    }
  }
  log.debug("mergePartsList", mergePartsList);

  return (
    <>
      {/* 部品情報セクション */}
      <s.PartsTitle>
        部品情報
        {isAfterCarryTaxinDate && (
          <span style={{ color: "#0000ff", fontSize: "12pt" }}>　※上記の車両情報（グレード、カラ―等）につきましては、念の為、車台番号から再度ご確認のうえ、お見積りをお願い致します。</span>
        )}
      </s.PartsTitle>

      {isInsuranceRequest && <s.InsuranceWarning>＜損保からの依頼分＞</s.InsuranceWarning>}

      <s.PLTable>
        <tbody>
          {mergePartsList.map((_partsItem, _partsIndex) => {
            const { id, is_add_parts, OthParts4CD, P_PartsName } = _partsItem;
            const target_key = is_add_parts ? OthParts4CD : id;

            const _partsAnsItem = safePartsData[target_key] || [];
            //const partsItem = _partsAnsItem[0] || _partsItem;
            const partsItem = _partsItem;
            const isPhoto = _partsItem.isPhoto;

            const answerData = data.Answer?.[_partsIndex] || [];
            const partsId = partsItem?.id;
            const PartsNameHeader = isGensha ? s.PartsNameHeaderGensha : s.PartsNameHeaderNormal;

            log.debug(`_partsIndex:${_partsIndex} _partsItem`, _partsItem);
            return (
              <React.Fragment key={_partsIndex}>
                {/* 部品名ヘッダー行 */}
                <tr>
                  <PartsNameHeader colSpan="13" scope="row">
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                      <span>{partsItem?.P_PartsName || partsItem?.OthPartsName}</span> {/* partsItem?.PartsAnswer?.OthPartsName */}
                      <div>
                        {/* 部品カテゴリアイコン */}
                        {(() => {
                          let pk3flg = 0;
                          const pkids = [partsItem?.P_PKID1, partsItem?.P_PKID2, partsItem?.P_PKID3, partsItem?.P_PKID4, partsItem?.P_PKID5];
                          //log.debug("pkids", pkids);
                          //log.debug("partskind", partskind);

                          return (
                            <>
                              {pkids.map((pkid, idx) => {
                                if (!pkid || pkid === 3) {
                                  if (pkid === 3) pk3flg = 1;
                                  return null;
                                }
                                const categoryName = formProps.getCategoryName(pkid, partskind);
                                const categoryStyle = formProps.getCategoryStyle(pkid);
                                return (
                                  <s.PartsCategoryIcon key={idx} style={categoryStyle}>
                                    {categoryName}
                                  </s.PartsCategoryIcon>
                                );
                              })}
                              {pk3flg === 1 && (
                                <>
                                  <span style={{ marginLeft: "10px" }}>　　</span>
                                  <s.PartsCategoryIcon style={formProps.getCategoryStyle(3)}>{formProps.getCategoryName(3, partskind)}</s.PartsCategoryIcon>
                                </>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    {/* 部品備考 */}
                    {partsItem?.Bikou && <span style={{ fontSize: "14px", fontWeight: "normal" }}>({partsItem.Bikou})</span>}
                  </PartsNameHeader>
                </tr>

                {/* テーブルヘッダー行 */}
                <tr>
                  <s.PLHeadTh2>&nbsp;</s.PLHeadTh2>
                  <s.PLHeadTh2 scope="row">純正品番</s.PLHeadTh2>
                  <s.PLHeadTh2>数量</s.PLHeadTh2>
                  <s.PLHeadTh2>部品カテゴリ</s.PLHeadTh2>
                  <s.PLHeadTh2>価格(税別)</s.PLHeadTh2>
                  {isAfterCarryTaxinDate ? (
                    <s.DeliveryHeaderTaxEx>送料(税別)</s.DeliveryHeaderTaxEx>
                  ) : (
                    <s.PLHeadTh2>
                      送料
                      <span style={{ fontWeight: "bold", backgroundColor: "rgb(255, 255, 0)" }}>(税込)</span>
                    </s.PLHeadTh2>
                  )}
                  <s.PLHeadTh2>発送可能日</s.PLHeadTh2>
                  <s.PLHeadTh2>見積有効期限</s.PLHeadTh2>
                  <s.PLHeadTh2>カラー番号</s.PLHeadTh2>
                  <s.PLHeadTh2>カラー名称</s.PLHeadTh2>
                  <s.PLHeadTh2>写真</s.PLHeadTh2>
                  <s.PLHeadTh2>部品備考</s.PLHeadTh2>
                  <s.PLHeadTh2 scope="row">自社管理番号</s.PLHeadTh2>
                </tr>

                {/* 回答データ行 */}
                {(() => {
                  let hasDisplay = false;
                  const rows = [];
                  log.debug("partsData", partsData);
                  const _partsChildList = safePartsData[target_key];

                  if (_partsChildList && Array.isArray(_partsChildList)) {
                    _partsChildList.forEach((_partsItem, key) => {
                      log.debug("_partsItem", _partsItem);
                      const lineSeq = Number(_partsItem?.Seqnum) || key + 1;
                      if (_partsItem?.StanderdPrice) {
                        hasDisplay = true;
                        const { jyotai, bgcolor } = formProps.getHacchuStatus(_partsItem?.HacchuFlg);
                        const isNonegr = bgcolor === "nonegr";
                        const RowTag = isNonegr ? s.NonegrRow : "tr";
                        log.debug("_partsItem", _partsItem);
                        log.debug(`isNonegr:${isNonegr}`);
                        rows.push(
                          <RowTag key={key}>
                            <s.ConfirmTableCell>{jyotai}</s.ConfirmTableCell>
                            <s.ConfirmTableCell scope="row">{_partsItem.PartsJyunseiCode || ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell>1</s.ConfirmTableCell>
                            <s.ConfirmTableCell>{formProps.getCategoryName(_partsItem.PartsKindID, partskind)}</s.ConfirmTableCell>
                            <s.ConfirmTableCell>{formProps.formatNumber(_partsItem.StanderdPrice)}円</s.ConfirmTableCell>
                            <s.ConfirmTableCell>{formProps.formatNumber(_partsItem.Soryo)}円</s.ConfirmTableCell>
                            <s.ConfirmTableCell>{formProps.getHassoStatusText(_partsItem.Hasso)}</s.ConfirmTableCell>
                            <s.ConfirmTableCell>{formProps.formatDate(_partsItem.Sell_Limit) || ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell>{_partsItem.ColorNo || ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell>{_partsItem.ColorName || ""}</s.ConfirmTableCell>
                            <s.ConfirmTableCell>
                              {(() => {
                                // DMD_PartsAnswers / PictAnswers / FileAnswers の PRID は追加回答で 0。ヘッダの依頼行 id を渡すと取得できない。
                                const isPrid0Row =
                                  Number(_partsItem?.PRID) === 0 ||
                                  String(target_key || "").startsWith("add_parts_id-") ||
                                  Number(partsItem?.PRID) === 0 ||
                                  partsItem?.is_add_parts === true;
                                const dialogPrid = isPrid0Row ? 0 : Number(partsItem?.id);
                                const pictResKey = Number.isFinite(dialogPrid) ? dialogPrid : 0;
                                const hasPhoto =
                                  resPict?.[pictResKey]?.[lineSeq] !== undefined && resPict[pictResKey][lineSeq] !== null;
                                const _isPhoto = partsItem?.isPhoto || false;

                                return (
                                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                                    <a
                                      onClick={formProps.handlePhotoClick(photoDialogProps, quote_number, dialogPrid, lineSeq, Consts.WRECKER_PHOTO.KARI_REF)}
                                      style={{ cursor: "pointer" }}
                                    >
                                      <Image src={hasPhoto || _isPhoto ? "/images/camera_icon2.png" : "/images/camera_icon.png"} alt="写真" width={24} height={20} />
                                    </a>
                                    <a
                                      onClick={formProps.handleFileupClick(fileupDialogProps, quote_number, dialogPrid, lineSeq, Consts.WRECKER_PHOTO.KARI_REF)}
                                      style={{ cursor: "pointer" }}
                                    >
                                      <Image src="/images/file_icon.png" alt="ファイル" width={20} height={24} />
                                    </a>
                                  </div>
                                );
                              })()}
                            </s.ConfirmTableCell>
                            <s.ConfirmTableCell style={{ whiteSpace: "pre-line", textAlign: "left" }}>{_partsItem.Bikou || ""}</s.ConfirmTableCell>
                            {isNonegr ? (
                              <s.ConfirmTableCell scope="row">{_partsItem.JishaNo || ""}</s.ConfirmTableCell>
                            ) : (
                              <s.CompanyCodeCellBg scope="row">
                                {_partsItem.JishaNo || ""}
                                <br />
                                {/* @@ToDo: 要チェック　PrepKindが更新されていないため */}
                                {Number(_partsItem.PrepKind) === Number(Consts.GOODS.INHOUSE) && <label>自社品</label>}
                                {Number(_partsItem.PrepKind) === Number(Consts.GOODS.OTHER) && <label>他社品</label>}
                              </s.CompanyCodeCellBg>
                            )}
                          </RowTag>,
                        );
                      }
                    });
                  }

                  // 未回答行の表示
                  if (!hasDisplay) {
                    const _not_display_style = { background: "#C0C0C0" };
                    rows.push(
                      <s.NoneRow key="none">
                        <s.ConfirmTableCell style={_not_display_style} scope="row">
                          -
                        </s.ConfirmTableCell>
                        <s.ConfirmTableCell style={_not_display_style} scope="row">
                          -
                        </s.ConfirmTableCell>
                        <s.ConfirmTableCell style={_not_display_style}>-</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={_not_display_style}>-</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={_not_display_style}>-</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={_not_display_style}>-</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={_not_display_style}>-</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={_not_display_style}>-</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={_not_display_style}>-</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={_not_display_style}>-</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={_not_display_style}>-</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={_not_display_style}>-</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={_not_display_style}>未回答</s.ConfirmTableCell>
                      </s.NoneRow>,
                    );
                  }

                  return rows;
                })()}
              </React.Fragment>
            );
          })}
        </tbody>
      </s.PLTable>

      {/* 追加商品情報セクション */}
      <s.PartsTitle>
        追加商品情報
        {isAfterCarryTaxinDate && <span style={{ color: "#0000ff", fontSize: "12pt" }}></span>}
      </s.PartsTitle>

      {addPartsData && Array.isArray(addPartsData) && addPartsData.length > 0 && (
        <s.PLTable>
          <tbody>
            {addPartsData.map((addItem, key) => {
              if (!addItem?.StanderdPrice) return null;

              log.debug("addItem", addItem);
              return (
                <React.Fragment key={key}>
                  {/* 追加商品名ヘッダー行 */}
                  <tr>
                    <s.PartsNameHeaderNormal colSpan="12" scope="row">
                      {addItem.OthPartsName || ""}
                      {addItem.OthPartsSonota && <>　品名：{addItem.OthPartsSonota}</>}
                    </s.PartsNameHeaderNormal>
                  </tr>

                  {/* テーブルヘッダー行 */}
                  <tr>
                    <s.PLHeadTh2 scope="row">純正品番</s.PLHeadTh2>
                    <s.PLHeadTh2>数量</s.PLHeadTh2>
                    <s.PLHeadTh2>部品カテゴリ</s.PLHeadTh2>
                    <s.PLHeadTh2>価格(税別)</s.PLHeadTh2>
                    {isAfterCarryTaxinDate ? (
                      <s.DeliveryHeaderTaxEx>送料(税別)</s.DeliveryHeaderTaxEx>
                    ) : (
                      <s.PLHeadTh2>
                        送料
                        <span style={{ fontWeight: "bold", backgroundColor: "rgb(255, 255, 0)" }}>(税込)</span>
                        <br />
                        <s.RequiredMark src="/images/must.png" alt="必須" />
                      </s.PLHeadTh2>
                    )}
                    <s.PLHeadTh2>発送可能日</s.PLHeadTh2>
                    <s.PLHeadTh2>見積有効期限</s.PLHeadTh2>
                    <s.PLHeadTh2>カラー番号</s.PLHeadTh2>
                    <s.PLHeadTh2>カラー名称</s.PLHeadTh2>
                    <s.PLHeadTh2>写真</s.PLHeadTh2>
                    <s.PLHeadTh2>部品備考</s.PLHeadTh2>
                    <s.PLHeadTh2 scope="row">自社管理番号</s.PLHeadTh2>
                  </tr>

                  {/* 追加商品データ行 */}
                  <tr>
                    <s.ConfirmTableCell scope="row">{addItem?.PartsJyunseiCode || ""}</s.ConfirmTableCell>
                    <s.ConfirmTableCell>1</s.ConfirmTableCell>
                    <s.ConfirmTableCell>{formProps.getCategoryName(addItem?.PartsKindID, partskind)}</s.ConfirmTableCell>
                    <s.ConfirmTableCell>{formProps.formatNumber(addItem?.StanderdPrice)}円</s.ConfirmTableCell>
                    <s.ConfirmTableCell>{formProps.formatNumber(addItem?.Soryo)}円</s.ConfirmTableCell>
                    <s.ConfirmTableCell>{formProps.getHassoStatusText(addItem?.Hasso)}</s.ConfirmTableCell>
                    <s.ConfirmTableCell>{formProps.formatDate(addItem?.Sell_Limit) || ""}</s.ConfirmTableCell>
                    <s.ConfirmTableCell>{addItem?.ColorNo || ""}</s.ConfirmTableCell>
                    <s.ConfirmTableCell>{addItem?.ColorName || ""}</s.ConfirmTableCell>
                    <s.ConfirmTableCell>
                      {(() => {
                        const seqnum = addItem?.Seqnum;
                        const hasPhoto = resPict?.[0]?.[seqnum] !== undefined && resPict[0][seqnum] !== null;
                        const iconName = hasPhoto ? "camera_icon2.png" : "camera_icon.png";
                        log.debug("check addItem", addItem);

                        return (
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                            <a
                              onClick={formProps.handlePhotoClick(
                                photoDialogProps,
                                quote_number,
                                0,
                                addItem?.Seqnum ?? key + 1,
                                Consts.WRECKER_PHOTO.KARI_REF,
                                addItem.OthPartsName,
                              )}
                              style={{ cursor: "pointer" }}
                            >
                              <Image src={hasAddPartsPhoto ? "/images/camera_icon2.png" : "/images/camera_icon.png"} alt="写真" width={24} height={20} />
                            </a>
                            <a
                              onClick={formProps.handleFileupClick(
                                fileupDialogProps,
                                quote_number,
                                0,
                                addItem?.Seqnum ?? key + 1,
                                Consts.WRECKER_PHOTO.KARI_REF,
                                addItem.OthPartsName,
                              )}
                              style={{ cursor: "pointer" }}
                            >
                              <Image src="/images/file_icon.png" alt="ファイル" width={20} height={24} />
                            </a>
                          </div>
                        );
                      })()}
                    </s.ConfirmTableCell>
                    <s.ConfirmTableCell style={{ whiteSpace: "pre-line", textAlign: "left" }}>{addItem?.Bikou || ""}</s.ConfirmTableCell>
                    <s.CompanyCodeCellBg scope="row">
                      {addItem?.JishaNo || ""}
                      <br />
                      {Number(addItem?.PrepKind) === Number(Consts.GOODS.INHOUSE) && <label>自社品</label>}
                      {Number(addItem?.PrepKind) === Number(Consts.GOODS.OTHER) && <label>他社品</label>}
                    </s.CompanyCodeCellBg>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </s.PLTable>
      )}
    </>
  );
}
